import type { ActivityLogicPlugin } from '../../core/types/plugin.base.types';
import type { InfoBlockPayload, InfoBlockRuntime } from '../../core/types/payloads/info_block.types';
import type { HostActionPayload, StudentInputEnvelope } from '../../core/types/events.types';
import type { RoutingMode } from '../../core/types/room.types';
import { normalizeFigureLegendEntries } from '../../core/music/figureLegendNormalize';

export function validateInfoBlockPayload(raw: unknown): InfoBlockPayload {
  if (!raw || typeof raw !== 'object') throw new Error('InfoBlock payload must be an object');
  const data = raw as Partial<InfoBlockPayload>;
  const title = String(data.title || data.sectionTitle || '').trim();
  const bodyText = String(data.bodyText || '').trim();
  if (!title) throw new Error('InfoBlock requires title');
  if (!bodyText) throw new Error('InfoBlock requires bodyText');
  const layout = data.layout === 'callout' || data.layout === 'context' ? data.layout : 'text';
  const figureLegend = Array.isArray(data.figureLegend)
    ? data.figureLegend.map(String).filter(Boolean)
    : undefined;
  const visualTokenReference = Array.isArray(data.visualTokenReference)
    ? data.visualTokenReference.map(String)
    : undefined;
  const formulaLayout = data.formulaLayout === 'fraction-percent' ? 'fraction-percent' : undefined;
  const figureLegendEntries = normalizeFigureLegendEntries(data.figureLegendEntries);
  const imagePrompt = data.imagePrompt ? String(data.imagePrompt).trim() : undefined;
  const wikiQuery = data.wikiQuery ? String(data.wikiQuery).trim() : undefined;
  const imageUrl = data.imageUrl ? String(data.imageUrl).trim() : undefined;
  return {
    title,
    sectionTitle: data.sectionTitle ? String(data.sectionTitle) : title,
    bodyText,
    layout,
    figureLegend,
    figureLegendEntries,
    visualTokenReference: figureLegend?.length ? undefined : visualTokenReference,
    timeSignature: data.timeSignature ? String(data.timeSignature) : undefined,
    teacherScript: data.teacherScript ? String(data.teacherScript) : undefined,
    formulaLayout,
    formulaNumeratorLabel: formulaLayout
      ? String(data.formulaNumeratorLabel || 'Valor Final − Valor Inicial').trim()
      : undefined,
    formulaDenominatorLabel: formulaLayout
      ? String(data.formulaDenominatorLabel || 'Valor Inicial').trim()
      : undefined,
    formulaSuffix: formulaLayout ? String(data.formulaSuffix || '× 100').trim() : undefined,
    ...(wikiQuery ? { wikiQuery } : {}),
    ...(imagePrompt && !wikiQuery ? { imagePrompt } : {}),
    ...(imageUrl ? { imageUrl } : {}),
  };
}

export function createInitialInfoBlockRuntime(_payload: InfoBlockPayload, _routing: RoutingMode): InfoBlockRuntime {
  return { viewed: false };
}

export const infoBlockLogic: ActivityLogicPlugin<'info_block'> = {
  mode: 'info_block',
  validatePayload: validateInfoBlockPayload,
  createInitialRuntime: createInitialInfoBlockRuntime,

  onStudentInput(ctx, input: StudentInputEnvelope) {
    if (input.action === 'EXAM_CONTINUE' || input.action === 'VIEW') {
      return { runtime: { ...ctx.runtime, viewed: true } };
    }
    return { runtime: ctx.runtime, rejected: true, rejectReason: 'UNKNOWN_ACTION' };
  },

  onHostAction(ctx, _action: HostActionPayload) {
    return { runtime: ctx.runtime };
  },
};

/** Demo neutro — sin vocabulario de ninguna asignatura (evita contaminación cruzada). */
export function demoInfoBlockPayload(): InfoBlockPayload {
  return validateInfoBlockPayload({
    title: 'Contexto del tema',
    sectionTitle: 'Introducción',
    bodyText:
      'En esta sección se presenta el contexto del tema: ideas clave, motivación y el punto de partida para las actividades siguientes.',
    layout: 'context',
  });
}

/** Demo exclusivo de música — solo para scaffold con partitura cargada. */
export function demoMusicInfoBlockPayload(): InfoBlockPayload {
  return validateInfoBlockPayload({
    title: 'Contexto de la obra',
    sectionTitle: 'Contexto de la obra',
    bodyText:
      'Esta pieza usa compases regulares. Observa cómo las figuras rítmicas se combinan para llenar cada compás.',
    layout: 'context',
  });
}
